import React, { useContext, useState } from "react";
import {
    archiveProduct,
    createProduct,
    getProduct,
    getProductCategories,
    unarchiveProduct,
    updateProduct,
} from "../../lib/api";
import { IAgeLevel, IProduct, IProductCategory } from "../../types/api";
import { useLoaderData, useLocation, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { displayFormAlert, getUserFromLocalStorage } from "../../lib/utils";
import * as yup from "yup";
import {
    Controller,
    SubmitErrorHandler,
    SubmitHandler,
    useForm,
} from "react-hook-form";
import { ICreateProductData } from "../../types/forms";
import { alertActions } from "../../features/alert/alertSlice";
import { Button, ButtonGroup, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import { store } from "../..";
import {
    globalActions,
    selectAgeLevels,
    selectSetting,
} from "../../features/global/globalSlice";
import { yupResolver } from "@hookform/resolvers/yup";
import DatePicker from "react-datepicker";
import { ProductsContext } from "./Products";

const ArchiveProductModal = ({
    show,
    setShow,
    action,
    handleAction,
    productId,
}: {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    action: string;
    handleAction: (action: string, productId: IProduct["id"]) => Promise<void>;
    productId: IProduct["id"];
}) => {
    let body: React.JSX.Element = <></>;

    switch (action) {
        case "archive":
            body = (
                <>
                    <h5 className="text-danger text-center">
                        You are about to archive this product!
                    </h5>
                    <p className="text-center">
                        This will deactivate it and remove it from admin views
                        (it can always be unarchived later if you wish)
                    </p>
                </>
            );
            break;
        case "unarchive":
            body = (
                <>
                    <h5 className="text-danger text-center">
                        You are about to unarchive this product!
                    </h5>
                    <p className="text-center">
                        This will return it to admin views set to an inactive
                        status so you can edit and use it again.
                    </p>
                </>
            );
            break;
    }

    return (
        <Modal show={show} onHide={() => setShow(false)}>
            <Modal.Header>
                <Modal.Title>{`${
                    action.charAt(0).toUpperCase() + action.slice(1)
                } Product`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{body}</Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShow(false)}>
                    No
                </Button>
                <Button
                    variant="danger"
                    onClick={() => {
                        handleAction(action, productId);
                    }}
                >
                    Yes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const loader = async ({ params }: any) => {
    const { productId } = params;

    const ageLevels = store.getState().global.ageLevels;
    const settings = store.getState().global.settings;

    const ageLevelsPromise = ageLevels.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getAgeLevels());

    const settingsPromise = settings.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getSettings());

    const getProductPromise = productId
        ? getProduct(parseInt(productId))
        : Promise.resolve();

    const [product, { categories }] = await Promise.all([
        getProductPromise,
        getProductCategories(),
        ageLevelsPromise,
        settingsPromise,
    ]);

    return {
        product: product?.product || undefined,
        categories,
        header: productId ? "Edit Product" : "Add Product",
    };
};

const Product = (): React.JSX.Element => {
    const { product: loadedProduct, categories } = useLoaderData() as {
        product: IProduct | undefined;
        categories: IProductCategory[];
    };

    const location = useLocation();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = getUserFromLocalStorage();
    const ageLevels = useAppSelector(selectAgeLevels);
    const { products, setProducts } = useContext(ProductsContext);
    const [product, setProduct] = useState<IProduct | undefined>(loadedProduct);
    const [showModal, setShowModal] = useState<boolean>(false);

    const createProductFormSchema = yup.object().shape({
        tournamentId: yup
            .number()
            .optional()
            .transform((value, originalValue) => {
                return originalValue === "" ? undefined : value;
            }),
        year: yup
            .number()
            .min(new Date().getFullYear(), "Year must be this year or later")
            .required("Year is required")
            .typeError("Invalid Year"),
        category: yup
            .object()
            .required()
            .shape({
                value: yup.number().required(),
                label: yup
                    .string()
                    .required("Please select the product category"),
            }),
        ageLevel: yup
            .object()
            .required()
            .shape({
                value: yup.number().required(),
                label: yup
                    .string()
                    .required("Please select the tournament age level"),
            }),
        qty: yup
            .number()
            .required("Please set the max number of teams for this tournament"),
        price: yup
            .number()
            .required("Please set the product price")
            .typeError("Invalid Price"),
        active: yup.number().oneOf([0, 1]).required(),
        startDate: yup.date().optional().nullable(),
        endDate: yup.date().optional().nullable(),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        watch,
        setValue,
        getValues,
    } = useForm<ICreateProductData>({
        mode: "onChange",
        shouldUnregister: true,
        resolver: yupResolver(createProductFormSchema),
        defaultValues: {
            tournamentId: product?.meta?.tournamentId,
            active: product?.active,
            ageLevel: {
                value: ageLevels.find(a => a.ageLevel === product?.name)?.id,
                label: product?.name,
            },
            category: {
                value: product?.category.id,
                label: product?.category.categoryName,
            },
            price: product?.price || undefined,
            qty: product?.max,
            startDate: product?.startDate ? new Date(product.startDate) : null,
            endDate: product?.endDate ? new Date(product.endDate) : null,
            year: product?.meta?.year,
        },
    });

    const onSubmit: SubmitHandler<ICreateProductData> = async data => {
        await updateProduct(data, product!.id, user!.id)
            .then(response => {
                dispatch(
                    alertActions.success({
                        title: "Product updated successfully",
                        dismissible: true,
                    }),
                );
                setProduct(response.products.find(p => p.id === product?.id));
                setProducts(response.products);
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem creating the product.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const onError: SubmitErrorHandler<ICreateProductData> = data => {
        console.log(data);
    };

    const handleArchive = async (action: string, productId: IProduct["id"]) => {
        action === "archive"
            ? await archiveProduct(productId, user!.id)
                  .then(response => {
                      dispatch(
                          alertActions.success({
                              title: "Product archived successfully",
                              dismissible: true,
                              persist: true,
                          }),
                      );
                      setProducts(response.products);
                      navigate("/tournament-management/products");
                  })
                  .catch(e => {
                      dispatch(
                          alertActions.error({
                              title: "The server encountered a problem archiving the product.",
                              text: "If the problem continues, please reach out to an admin.",
                              dismissible: true,
                          }),
                      );
                  })
            : await unarchiveProduct(productId, user!.id)
                  .then(response => {
                      dispatch(
                          alertActions.success({
                              title: "Product unarchived successfully",
                              dismissible: true,
                              persist: true,
                          }),
                      );
                      setProducts(response.products);
                      navigate("/tournament-management/products");
                  })
                  .catch(e => {
                      dispatch(
                          alertActions.error({
                              title: "The server encountered a problem unarchiving the product.",
                              text: "If the problem continues, please reach out to an admin.",
                              dismissible: true,
                          }),
                      );
                  });
    };

    const isProductActive = watch("active", product?.active);

    const isArchived = product?.active === -1;

    return (
        <section
            className="createProductInfo"
            data-testid="createProductContent"
        >
            <Form
                onSubmit={handleSubmit(onSubmit, onError)}
                id="createGameForm"
            >
                <Row>
                    <Form.Control {...register("tournamentId")} type="hidden" />
                    <Form.Group controlId="year" as={Col} sm={6} lg={4}>
                        <Form.Label>Year</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("year")}
                            aria-invalid={errors.year ? "true" : "false"}
                            disabled={isArchived}
                        />
                        {errors.year &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.year?.message as string,
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId="category"
                        as={Col}
                        sm={6}
                        md={6}
                        lg={4}
                    >
                        <Form.Label>Category</Form.Label>
                        <Controller
                            name="category"
                            control={control}
                            rules={{
                                required: "Category is required",
                            }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isDisabled={isArchived}
                                    options={categories.map(c => {
                                        return {
                                            value: parseInt(c.id.toString()),
                                            label: c.categoryName,
                                        };
                                    })}
                                />
                            )}
                        />
                        {errors.category &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.category?.label?.message as string,
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId="ageLevel"
                        as={Col}
                        sm={6}
                        md={6}
                        lg={4}
                    >
                        <Form.Label>Age Level</Form.Label>
                        <Controller
                            name="ageLevel"
                            control={control}
                            rules={{
                                required: "Age level is required",
                            }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isSearchable={true}
                                    isDisabled={isArchived}
                                    options={ageLevels.map(a => {
                                        return {
                                            value: parseInt(a.id.toString()),
                                            label: a.ageLevel,
                                        };
                                    })}
                                />
                            )}
                        />
                        {errors.ageLevel &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.ageLevel?.label?.message as string,
                            })}
                    </Form.Group>
                    <Form.Group controlId="qty" as={Col} xs={6} lg={4}>
                        <Form.Label>Max Teams </Form.Label>
                        <Form.Control
                            type="text"
                            {...register("qty")}
                            aria-invalid={errors.qty ? "true" : "false"}
                            disabled={isArchived}
                        />
                        {errors.qty &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.qty.message as string,
                            })}
                    </Form.Group>
                    <Form.Group controlId="price" as={Col} xs={6} lg={4}>
                        <Form.Label>Price</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("price")}
                            aria-invalid={errors.price ? "true" : "false"}
                            disabled={isArchived}
                        />
                        {errors.price &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.price.message as string,
                            })}
                    </Form.Group>
                </Row>
                <Row>
                    {!isArchived && (
                        <Form.Group
                            controlId="active"
                            as={Col}
                            xs={{ order: "last" }}
                            md={{ span: 4, order: "first" }}
                        >
                            <Form.Control
                                type="hidden"
                                {...register("active", {
                                    value: isProductActive,
                                })}
                            />
                            <ButtonGroup>
                                <Button
                                    variant={
                                        isProductActive
                                            ? "primary"
                                            : "outline-primary"
                                    }
                                    onClick={() => setValue("active", 1)}
                                    disabled={isArchived}
                                >
                                    Active
                                </Button>
                                <Button
                                    variant={
                                        isProductActive
                                            ? "outline-primary"
                                            : "primary"
                                    }
                                    onClick={() => setValue("active", 0)}
                                    disabled={isArchived}
                                >
                                    Inactive
                                </Button>
                            </ButtonGroup>
                        </Form.Group>
                    )}
                    <Form.Group controlId="startDate" as={Col} sm={6} md={4}>
                        <Form.Label>
                            Product Start Date{" "}
                            <span className="fw-normal">(optional)</span>
                        </Form.Label>
                        <br />
                        <Controller
                            name="startDate"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    selected={watch(
                                        "startDate",
                                        product?.startDate
                                            ? new Date(product.startDate)
                                            : null,
                                    )}
                                    onChange={date => {
                                        setValue("startDate", date as Date, {
                                            shouldValidate: true,
                                        });

                                        const endDate = getValues("endDate");
                                        date &&
                                            endDate &&
                                            date > endDate &&
                                            setValue("endDate", null, {
                                                shouldValidate: true,
                                            });
                                    }}
                                    showMonthDropdown
                                    showYearDropdown
                                    showTimeSelect
                                    timeFormat="h:mm aa"
                                    timeIntervals={60}
                                    timeCaption="Time"
                                    dateFormat="MMMM d, yyyy h:mm aa"
                                    dropdownMode="select"
                                    className="form-control"
                                    isClearable
                                    disabled={isArchived}
                                />
                            )}
                        />
                        {errors.startDate &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.startDate?.message as string,
                                settings: "mb-0",
                            })}
                    </Form.Group>
                    <Form.Group controlId="endDate" as={Col} sm={6} md={4}>
                        <Form.Label>
                            Product End Date{" "}
                            <span className="fw-normal">(optional)</span>
                        </Form.Label>
                        <br />
                        <Controller
                            name="endDate"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    selected={watch(
                                        "endDate",
                                        product?.endDate
                                            ? new Date(product.endDate)
                                            : null,
                                    )}
                                    onChange={date =>
                                        setValue("endDate", date as Date, {
                                            shouldValidate: true,
                                        })
                                    }
                                    showMonthDropdown
                                    showYearDropdown
                                    showTimeSelect
                                    timeFormat="h:mm aa"
                                    timeIntervals={60}
                                    timeCaption="Time"
                                    dateFormat="MMMM d, yyyy h:mm aa"
                                    dropdownMode="select"
                                    className="form-control"
                                    isClearable
                                    minDate={
                                        getValues("startDate")
                                            ? getValues("startDate")
                                            : null
                                    }
                                    disabled={isArchived}
                                />
                            )}
                        />
                        {errors.endDate &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.endDate?.message as string,
                                settings: "mb-0",
                            })}
                    </Form.Group>
                </Row>
                {!isArchived && (
                    <Row>
                        <Col>
                            <Button
                                variant="primary"
                                type="submit"
                                className="mt-4"
                                disabled={isArchived}
                            >
                                Update Product
                            </Button>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col>
                        <Button
                            variant="danger"
                            type="button"
                            className="mt-4"
                            onClick={() => setShowModal(true)}
                        >
                            {product?.active !== -1 ? "Archive" : "Unarchive"}{" "}
                            Product
                        </Button>
                    </Col>
                </Row>
                <ArchiveProductModal
                    show={showModal}
                    setShow={setShowModal}
                    productId={product?.id as IProduct["id"]}
                    action={product?.active !== -1 ? "archive" : "unarchive"}
                    handleAction={handleArchive}
                />
            </Form>
        </section>
    );
};

export default Product;
